import React, { useState } from "react";
import { useDMAnalytics } from "../../hooks/useDMAnalytics";
import { SampleType } from "../../hooks/useDMSamples";
import DMSampleModal from "./DMSampleModal";
import { ConversationViewModal } from "./ConversationViewModal";
import { DMHealthMethodologyModal } from "./DMHealthMethodologyModal";
import { SetterProofModal } from "./SetterProofModal";
import { BucketSummary } from "./proof/BucketSummary";
import { getBucketColor } from "./proof/bucketColors";
import { useOrgSetterIds } from "../../hooks/useOrgSetterIds";
import { useReplyTurnDistributionAll } from "../../hooks/useReplyTurnDistributionAll";
import { useEngagedPerSetter } from "../../hooks/useEngagedPerSetter";
import type { ProofMetricType } from "../../types/core";
import DMHealthHeroCards from "./DMHealthHeroCards";
import DMHealthDailyActivity from "./DMHealthDailyActivity";
import CountUp from "../ui/CountUp";
import { useAnimGate } from "../../hooks/useAnimGate";

// Local SVGAreaChart helper extracted to ./SVGAreaChart so the chart engine can
// be reused by DMHealthDailyActivity (and any future shared DM widget).
// Hero cards extracted to ./DMHealthHeroCards (reused in /admin/portal).
// Daily Activity chart extracted to ./DMHealthDailyActivity (reused in /admin/portal).

// =============================================================================
// DM HEALTH TAB
// =============================================================================
// PURPOSE: Displays the DM Health view of DM Analytics - funnel buckets,
// volume stats, charts, and recent conversations table.
//
// This is Tab 2 of the DM Analytics page (Tab 1 = Setter Performance).
// Extracted from DMAnalyticsPage.tsx for V3 two-tab structure.
//
// SECTIONS:
// 1. Hero Cards (top-level KPIs)
// 2. Daily Activity chart (full width, directly under the hero cards)
// 3. Reply Turn Distribution (org-wide aggregate)
// =============================================================================

interface DMHealthTabProps {
  organizationId: string;
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
}

const DMHealthTab: React.FC<DMHealthTabProps> = ({ organizationId, dateRange, timezone }) => {
  // Sample modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<SampleType>("new_dm");
  const [modalTitle, setModalTitle] = useState("");

  // Conversation view modal state
  const [selectedConversation, setSelectedConversation] = useState<{
    id: string;
    contactName: string;
    leadInstagram: string | null;
    highlightTimestamp?: string;
    anchorTimestamp?: string;
    // dm-swipe-nav (P1) — set only when opened from the proof grid.
    siblings?: import("../../hooks/data-fetching/useConversationPreviews").NavSibling[];
    index?: number;
    hasMore?: boolean;
  } | null>(null);

  // Methodology modal state
  const [methodologyModalOpen, setMethodologyModalOpen] = useState(false);

  // Reply Turn Distribution — all setters
  const { setterIds: allSetterIds } = useOrgSetterIds(organizationId);
  const { rows: replyTurnRows, loading: replyTurnLoading } = useReplyTurnDistributionAll({
    organizationId,
    setterIds: allSetterIds,
    dateRange,
    windowMode: "lifetime",
  });
  const { rows: engagedPerSetter } = useEngagedPerSetter({
    organizationId,
    dateRange,
  });
  const [proofSetterId, setProofSetterId] = useState<string | null>(null);
  const [proofMetricType, setProofMetricType] =
    useState<ProofMetricType>("reply_turn_distribution");

  // Fetch DM analytics data
  const { data, loading, error, chartData } = useDMAnalytics(organizationId, dateRange, timezone);

  const openSampleModal = (type: SampleType, title: string) => {
    setModalType(type);
    setModalTitle(title);
    setModalOpen(true);
  };

  // Calculate day count for per-day metrics (inclusive of both start and end dates).
  // Normalize both bounds to midnight before diffing — the incoming end is set to
  // 23:59:59.999 by DMAnalyticsPage's normalizedDateRange, which would make
  // Math.round((end - start) / DAY_MS) yield 1 for a single-day range (0.9999 → 1),
  // then +1 = 2 days. Robust against DST transitions too.
  const dayCount = (() => {
    if (!dateRange.start || !dateRange.end) return 1;
    const startMidnight = new Date(dateRange.start);
    startMidnight.setHours(0, 0, 0, 0);
    const endMidnight = new Date(dateRange.end);
    endMidnight.setHours(0, 0, 0, 0);
    return Math.max(
      1,
      Math.round((endMidnight.getTime() - startMidnight.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );
  })();

  // Scroll-gate: org-wide RT distribution bars (BucketSummary .anim-fill) hold
  // until the section scrolls into view. Declared before the early returns
  // below — hooks must run unconditionally.
  const [orgReplyTurnsRef, orgReplyTurnsGate] = useAnimGate<HTMLDivElement>();

  if (loading) {
    return (
      <div className="bg-ds-accent/20 border border-ds-accent/30 backdrop-blur-sm rounded-lg p-4 text-ds-accent">
        Loading DM analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-ds-red/20 border border-ds-red/30 backdrop-blur-sm rounded-lg p-4 text-ds-red">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SECTION 1: HERO CARDS — extracted to DMHealthHeroCards */}
      <DMHealthHeroCards
        data={data}
        dayCount={dayCount}
        onOpenSample={openSampleModal}
        engagedPerSetter={engagedPerSetter}
        replyTurnRows={replyTurnRows}
        onSetterBarClick={(setterId) => {
          setProofSetterId(setterId);
          setProofMetricType("unengaged_conversations");
        }}
        onShallowBarClick={() => {
          setProofSetterId("__ALL__");
          setProofMetricType("reply_turn_distribution");
        }}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 2: DAILY ACTIVITY — full width, directly under the hero cards.
          Extracted to DMHealthDailyActivity (also reused in /admin/portal). The
          "How metrics work" link opens the DM methodology modal (portal omits it).
          ═══════════════════════════════════════════════════════════════════════════ */}
      <DMHealthDailyActivity
        chartData={chartData}
        onHowItWorks={() => setMethodologyModalOpen(true)}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 3: REPLY TURN DISTRIBUTION — ORG-WIDE AGGREGATE
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div
        ref={orgReplyTurnsRef}
        className={`liquid-glass anim-lift rounded-xl p-6 ${orgReplyTurnsGate}`}
      >
        <div className="mb-5">
          <h3 className="font-semibold text-glass token-18">Reply Turn Distribution</h3>
          <p className="token-11 text-glass-muted mt-1">
            Org-wide conversation depth — every assigned conversation across all setters, bucketed
            by how many times the lead and setter bounced replies. Click any bar to drill into the
            conversations behind it.
          </p>
        </div>
        {replyTurnLoading && replyTurnRows.length === 0 ? (
          <div className="text-glass-muted token-12 py-8 text-center">
            Loading reply turn distribution…
          </div>
        ) : replyTurnRows.length === 0 ? (
          <div className="text-glass-muted token-12 py-8 text-center">
            No setter activity in this date range.
          </div>
        ) : (
          (() => {
            // Aggregate buckets across all setters (sum counts + leadCounts per bucket label)
            const bucketLabels = replyTurnRows[0]?.buckets.map((b) => b.bucket) || [];
            const aggregated = bucketLabels.map((label) => {
              let count = 0;
              let leadCount = 0;
              for (const row of replyTurnRows) {
                const b = row.buckets.find((x) => x.bucket === label);
                if (b) {
                  count += b.count;
                  leadCount += b.leadCount;
                }
              }
              return { bucket: label, count, leadCount, color: getBucketColor(label) };
            });
            const totalCount = replyTurnRows.reduce((acc, r) => acc + r.totalCount, 0);
            return (
              <BucketSummary
                setterName={
                  <>
                    <CountUp value={replyTurnRows.length} /> setters ·{" "}
                    <CountUp value={totalCount} format={(n) => Math.round(n).toLocaleString()} />{" "}
                    assigned conversations
                  </>
                }
                totalCount={totalCount}
                buckets={aggregated}
                onBucketClick={() => {
                  // Org-wide aggregate → drill to the modal showing all setters' conversations
                  // at this bucket. Modal handles multi-setter view via setterIds array.
                  setProofSetterId("__ALL__");
                }}
              />
            );
          })()
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════════════════════════════ */}

      {/* Reply Turn Distribution Proof Modal */}
      {proofSetterId && (
        <SetterProofModal
          isOpen={!!proofSetterId}
          onClose={() => {
            setProofSetterId(null);
            setProofMetricType("reply_turn_distribution");
          }}
          metricType={proofMetricType}
          organizationId={organizationId}
          setterIds={
            proofSetterId === "__ALL__"
              ? allSetterIds
              : proofSetterId === "__UNASSIGNED__"
                ? [] // sentinel — Step 7c gates useSetterProof when metricType === "unengaged_conversations", so [] is safe
                : [proofSetterId]
          }
          dateRange={dateRange}
          timezone={timezone}
          onConversationClick={(id, name, instagram, highlightTimestamps, nav) =>
            setSelectedConversation({
              id,
              contactName: name,
              leadInstagram: instagram,
              highlightTimestamp: highlightTimestamps?.highlight,
              anchorTimestamp: highlightTimestamps?.anchor,
              siblings: nav?.siblings,
              index: nav?.index,
              hasMore: nav?.hasMore,
            })
          }
        />
      )}

      {/* Sample Modal */}
      <DMSampleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        sampleType={modalType}
        title={modalTitle}
        organizationId={organizationId}
        dateRange={dateRange}
        timezone={timezone}
      />

      {/* Conversation View Modal */}
      {selectedConversation && (
        <ConversationViewModal
          isOpen={!!selectedConversation}
          onClose={() => setSelectedConversation(null)}
          conversationId={selectedConversation.id}
          contactName={selectedConversation.contactName}
          leadInstagram={selectedConversation.leadInstagram}
          organizationId={organizationId}
          timezone={timezone}
          highlightTimestamp={selectedConversation.highlightTimestamp}
          anchorTimestamp={selectedConversation.anchorTimestamp}
          activeDateRange={
            dateRange.start && dateRange.end ? { start: dateRange.start, end: dateRange.end } : null
          }
          siblings={selectedConversation.siblings}
          currentIndex={selectedConversation.index}
          onNavigate={(nextIndex) =>
            setSelectedConversation((prev) => (prev ? { ...prev, index: nextIndex } : prev))
          }
          siblingsHasMore={selectedConversation.hasMore}
          siblingsTotal={selectedConversation.siblings?.length}
        />
      )}

      <DMHealthMethodologyModal
        isOpen={methodologyModalOpen}
        onClose={() => setMethodologyModalOpen(false)}
      />
    </div>
  );
};

export default DMHealthTab;
